// src/components/solution_sections/WhyDopomogaiMultiAgentSection.tsx
import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

import { Card } from '@dopomogai/ui';
import { renderIcon } from '@dopomogai/utils';
import { INNER_CARD_ANIMATE_RESTING, INNER_CARD_HOVER, INNER_CARD_TRANSITION_HOVER } from '../../shared/animationConstants';

// Define the data shapes for this component
interface Strength {
    icon?: string;
    title: string;
    description: string;
}

interface WhyDopomogaiData {
    sectionTitle?: string;
    icon?: string;
    strengths: Strength[];
}

interface WhyDopomogaiMultiAgentSectionProps {
  data: WhyDopomogaiData;
  id: string;
  headerBgColor?: string;
}

const listVariants = {
  visible: { transition: { staggerChildren: 0.1 } },
  hidden: {},
};

const itemVariants = {
  visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: 'easeOut' } },
  hidden: { opacity: 0, y: 20 },
};

export const WhyDopomogaiMultiAgentSection = React.forwardRef<HTMLDivElement, WhyDopomogaiMultiAgentSectionProps>(({
  data,
  id,
  headerBgColor,
}, ref) => {
  const { t } = useTranslation('solution_showcase');

  if (!data || !data.strengths || data.strengths.length === 0) {
    return null;
  }

  return (
    <Card
      ref={ref}
      title={data.sectionTitle || t('whyDopomogai.defaultTitle')} // Use data title or fallback
      icon={data.icon || 'award'}
      id={id}
      headerBgColor={headerBgColor}
    >
      <motion.div
        className="grid grid-cols-1 md:grid-cols-3 gap-6"
        variants={listVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
      >
        {data.strengths.map((strength, index) => (
          <motion.div
            key={index}
            variants={itemVariants}
            animate={INNER_CARD_ANIMATE_RESTING}
            whileHover={INNER_CARD_HOVER}
            transition={INNER_CARD_TRANSITION_HOVER}
            className="bg-white p-6 rounded-xl border-2 border-black text-center"
          >
            <div className="inline-block bg-blue-100 text-blue-600 p-4 rounded-full border-2 border-black shadow-sm mb-4">
              {renderIcon(strength.icon || 'checkcircle', { size: 32 })}
            </div>
            <h4 className="text-lg font-semibold text-gray-900 mb-2">
                {strength.title} {/* <-- Renders title directly from data */}
            </h4>
            <p className="text-sm text-gray-600">
                {strength.description} {/* <-- Renders description directly from data */}
            </p>
          </motion.div>
        ))}
      </motion.div>
    </Card>
  );
});

